import React, { useEffect, useState } from "react";
import { useRouteMatch } from "react-router";
import { Box } from "@material-ui/core";
import _ from "lodash";

import { ERROR_MESSAGE_UNEXPECTED_ERROR } from "../../../../utils/consts";
import withAuthority from "../../../../components/Auth/withAuthority";
import Authorities from "../../../../auth/authorities";
import DefaultAlert from "../../../../components/alerts/DefaultAlert";
import { fetchAllFilterLocations } from "../../../../services/locationApp/locationFilterService";
import { getCookie } from "../../../../utils/cookies";
import PaymentReportInfoNode from "./PaymentReportInfoNode";
import { fetchAllPaymentReportInfo } from "../../../../services/salesApp/paymentReportService";
import {
  customerPageReportFilterObject,
  paymentFilterObject,
} from "../../../../utils/consts/list";
import SaleReport from "../../../../components/common/SaleReport";
import {
  fetchAllRewardChoicesInfo,
  fetchAllRewardRedemptionInfo,
} from "../../../../services/customerApp/reports";
import {
  fetchLoyaltyProgramInfo,
  fetchRewardsInfo,
} from "../../../../services/customerApp/customerDetails";
import { fetchAllStockItemsProductInfo } from "../../../../services/inventory/stockItems";
import { AxiosError } from "axios";

/**
 * RewardChoicesReport Component
 *
 * The RewardChoicesReport component is responsible for displaying the payment summary report
 * for a specific location within the PrestoExpress application. It utilizes Material-UI
 * components and various utility functions to fetch and display payment-related information.
 */
const RewardChoicesReport: React.FunctionComponent<{}> = () => {
  const [error, setError] = useState("");
  const [locationSelectorList, setLocationSelectorList] = useState([]);
  const [isGetLocationInfo, setIsGetLocationInfo] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [filterDetails, setFilterDetails] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [filterData, setFilterData] = useState("");
  const [basedOnShift, setBasedOnShift] = useState(false);
  const [openFilterCard, setOpenFilterCard] = useState(false);
  const [locationSelectedList, setLocationSelectedList] = useState([]);
  const [programId, setProgramId] = useState("");
  const [rewardRedeemedNodeList, setRewardRedeemedNodeList] = useState([]);
  const [menuProducts, setMenuProducts] = useState({});
  const [isFetchMenuProduct, setIsFetchMenuProduct] = useState(false);
  const [rewardNode, setRewardNode] = useState({});

  const match: any = useRouteMatch();
  const idToken = getCookie("idToken");

  const handleGetLocationProgram = async (filter: any) => {
    try {
      const { data } = await fetchLoyaltyProgramInfo(match.params.locationId);
      const programDetails: any = Object.values(data.data)[0];
      setProgramId(programDetails.programId);

      handleGetRewardsDetails(programDetails?.programId || "");
    } catch (error) {
      setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
    }
  };

  const handleRestructureProducts = (input: any) => {
    const output = Object.values(input).reduce((acc: any, item: any) => {
      const key = item.itemWrapperId;
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    }, {});
    return output;
  };

  const handleGetRewardsDetails = async (programId: any) => {
    try {
      const res = await fetchRewardsInfo(match.params.locationId, programId);
      setRewardNode(res.data.data);
    } catch (e) {
      setIsLoading(false);
      setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
    }
    setIsGetLocationInfo(true);
  };

  /**
   * Asynchronous function to fetch payment report information based on filter criteria.
   * It updates the component state with the fetched data or handles errors appropriately.
   */
  const getPaymentReportInfo = async (filter: any, programId: string) => {
    // Clear the existing payment report node list
    try {
      // Fetch payment report information from the server
      const response = await fetchAllRewardChoicesInfo(
        idToken,
        match.params.locationId,
        filter,
      );
      setRewardRedeemedNodeList(response.data.data);
      setIsLoading(false);

      // Update the state with the fetched data or an empty array if no data is available
    } catch (err) {
      setIsLoading(false);
      setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
    }
  };

  const handleGetStockItemsProductsInfo = async () => {
    try {
      // Attempt to fetch stock items products information using the 'fetchAllStockItemsProductInfo' API
      const res = await fetchAllStockItemsProductInfo(match.params.locationId);
      // Set the 'menuProducts' state with the retrieved data from the response
      const data: any = handleRestructureProducts(res.data.data);
      setMenuProducts(data);
      setIsFetchMenuProduct(true);
      handleGetLocationProgram("");
    } catch (error) {
      const err: any = error as AxiosError;
      if (err.response.status !== 404) {
        // If an error occurs during the API call, set the 'error' state with an unexpected error message
        setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
      }
    }
  };

  /**
   * Fetches and updates the location selector list based on the provided search name.
   *
   * @param {string} searchName - The search name used for filtering locations.
   */
  const getAllFilterLocation = async (searchName: any) => {
    try {
      // Fetch all filter locations using the provided search name
      const response = await fetchAllFilterLocations(searchName);

      // Process the fetched data to create a list of
      if (!_.isEmpty(response.data.data)) {
        const locationList = response.data.data.map((location: any) => ({
          id: location.id,
          label: location.businessDisplayName,
        }));

        // Update the location selector list and set the flag indicating location information is retrieved
        setLocationSelectorList(locationList);
      } else {
        setLocationSelectorList([]);
      }
      handleGetStockItemsProductsInfo();
    } catch (error) {
      // Handle errors by setting an error message
      setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
    }
  };

  /**
   * useEffect hook to set the document title and fetch initial location filter data.
   * It runs only once when the component mounts.
   */
  useEffect(() => {
    // Set the document title for the page
    document.title = "Sale - Reward Choices Report";

    // Fetch initial location filter data with an empty searchName
    getAllFilterLocation("");
  }, []);
  /**
   * Handles the filter data and triggers the update of the payment report.
   */
  const handleFilterData = (
    filterData: any,
    basedOnShift: boolean,
    count: any,
  ) => {
    setIsLoading(true);

    // Fetch and update the payment report information based on the provided filter data and shift flag

    getPaymentReportInfo(filterData, programId);

    // Set the filter data and shift flag
    setFilterData(filterData);
    setBasedOnShift(basedOnShift);
  };

  /**
   * Handles the click event on the text, opens the filter card, and scrolls to the top.
   */
  const handleOnClickText = () => {
    setOpenFilterCard(true);

    // Scroll to the top of the window with smooth behavior
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  /*Get location list API call after typing. */
  const handleLocationSelectorTypingList = (searchName: any) => {};

  return (
    <>
      <Box>
        <SaleReport
          handleFilterData={handleFilterData}
          locationSelectorList={locationSelectorList}
          handleLocationSelectorTypingList={handleLocationSelectorTypingList}
          isGetLocationInfo={isGetLocationInfo}
          setFilterDetails={setFilterDetails}
          availableFilter={customerPageReportFilterObject}
          isOpenSkeletonLoading={isLoading}
          nodeList={rewardRedeemedNodeList}
          filterDetails={filterDetails}
          title={""}
          isChart={false}
          filterTitle="Reward Choices report for"
          topic="Reward Choices Report"
          isNeedFilterDetails={true}
          locationSelectedList={locationSelectedList}
          setLocationSelectedList={setLocationSelectedList}
          openFilterCard={openFilterCard}
          setOpenFilterCard={setOpenFilterCard}
          handleOnClickText={handleOnClickText}
          isReport={true}
          payInOut={true}
          isInitialLoad={isInitialLoad}
          setIsInitialLoad={setIsInitialLoad}
          initialParamSize={5}
          errorTitle="Reward"
        >
          <PaymentReportInfoNode
            rewardRedeemedNodeList={rewardRedeemedNodeList}
            locationSelectorList={locationSelectorList}
            filterDetails={filterDetails}
            filterData={filterData}
            basedOnShift={basedOnShift}
            locationSelectedList={locationSelectedList}
            handleOnClickText={handleOnClickText}
            menuProducts={menuProducts}
            rewardNode={rewardNode}
          />
        </SaleReport>

        <DefaultAlert
          open={!!error}
          handleClose={() => setError("")}
          message={error}
          severity="error"
        />
      </Box>
    </>
  );
};

export default withAuthority(RewardChoicesReport, Authorities.SALE_READ);
